import React, { useMemo } from 'react';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { ProposalBundle } from '../api/proposalBundles';
import LatestProposals from './LatestProposals';

interface Props {
  trip: TripResponseArgs;
  proposalBundles?: ProposalBundle[];
}

const InfoPanel: React.FC<Props> = ({ trip, proposalBundles = [] }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const tripOverview = buildTripOverview(trip);

  return (
    <div style={styles.container}>
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>このチャットの最新の提案内容</h3>
        <LatestProposals bundles={proposalBundles} />
      </section>
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>出張申請に関する情報</h3>
          <button type="button" style={styles.linkButton}>
            編集
          </button>
        </div>
        <ul style={styles.list}>
          <li>出張先: {tripOverview.destination}</li>
          <li>出張の目的: {tripOverview.purpose}</li>
          <li>社内管理番号: {tripOverview.internalNumber}</li>
          <li>費用負担部署: {tripOverview.department}</li>
          <li>プロジェクト: {tripOverview.project}</li>
        </ul>
      </section>
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>予約手配に関する情報</h3>
          <button type="button" style={styles.linkButton}>
            編集
          </button>
        </div>
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>宿泊情報</div>
          <div style={styles.collapsibleBody}>氏名や緊急連絡先などがここに表示されます。</div>
        </div>
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>航空券搭乗情報</div>
          <div style={styles.collapsibleBody}>搭乗者の詳細情報がここに表示されます。</div>
        </div>
      </section>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    width: '320px',
    padding: '20px',
    backgroundColor: theme.contentBgColor,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px'
  },
  section: {
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: theme.whiteColor
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600 as const,
    margin: 0
  },
  list: {
    listStyle: 'none' as const,
    padding: 0,
    margin: '12px 0 0',
    lineHeight: 1.8,
    fontSize: '13px'
  },
  linkButton: {
    border: 'none',
    background: 'none',
    color: theme.linkColor,
    cursor: 'pointer'
  },
  collapsible: {
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '6px',
    marginTop: '12px'
  },
  collapsibleHeader: {
    padding: '10px 12px',
    backgroundColor: theme.contentBgColor,
    fontWeight: 600 as const
  },
  collapsibleBody: {
    padding: '12px',
    color: theme.grayTextColor,
    fontSize: '13px'
  }
});

const EMPTY_VALUE = '未記入';

const buildTripOverview = (trip: TripResponseArgs) => {
  const destination = trip.final_destination || EMPTY_VALUE;
  const purpose = trip.purpose || EMPTY_VALUE;
  const internalNumber = trip.internal_number || EMPTY_VALUE;
  const departmentName = trip.charging_departments?.[0]?.name;
  const projectName = trip.projects?.[0]?.name;

  return {
    destination,
    purpose,
    internalNumber,
    department: departmentName || EMPTY_VALUE,
    project: projectName || EMPTY_VALUE
  };
};

export default InfoPanel;
